<template>
  <div class="login-view">
    <form class="login-card" @submit.prevent="onSubmit">
      <div class="login-card__logo">
        <ParkosMark :size="30" />
        <span class="login-card__logo-text">Parkos</span>
      </div>

      <h1 class="login-card__title">Iniciar sesión</h1>
      <p class="login-card__subtitle">Ingresa con tu correo y contraseña.</p>
      <p v-if="route.query.expired" class="login-card__notice">Tu sesión expiró. Inicia sesión de nuevo para continuar.</p>

      <label class="login-card__label" for="login-email">Correo</label>
      <input
        id="login-email"
        v-model.trim="email"
        type="email"
        class="login-card__input"
        autocomplete="username"
        required
      />

      <label class="login-card__label" for="login-password">Contraseña</label>
      <input
        id="login-password"
        v-model="password"
        type="password"
        class="login-card__input"
        autocomplete="current-password"
        required
      />

      <p v-if="errorMessage" class="login-card__error">{{ errorMessage }}</p>

      <button type="submit" class="login-card__submit" :disabled="submitting">
        <span v-if="submitting" class="mdi mdi-loading login-card__spinner"></span>
        <span>{{ submitting ? 'Ingresando...' : 'Ingresar' }}</span>
      </button>
      <router-link class="login-card__forgot" :to="{ path: '/olvide-contrasena', query: email ? { email } : {} }">¿Olvidaste tu contraseña?</router-link>
      <router-link class="login-card__forgot" to="/registro">¿Primera vez? Crea tu cuenta</router-link>
    </form>
  </div>
</template>

<script setup>
import ParkosMark from '@/components/Global/ParkosMark.vue'
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/services/authStore'

const route = useRoute()
const router = useRouter()
const { login } = useAuthStore()

const email = ref('')
const password = ref('')
const submitting = ref(false)
const errorMessage = ref('')

const ERROR_MESSAGES = {
  INVALID_CREDENTIALS: 'Correo o contraseña incorrectos.',
  ACCOUNT_NOT_ACTIVE: 'Esta cuenta no está activa. Contacta a un administrador.',
  MISSING_FIELDS: 'Ingresa tu correo y contraseña.',
  PARK_SUSPENDED: 'El acceso de tu parque está en pausa (por ejemplo, porque terminó tu prueba). Escríbenos a contacto@parkosindustrial.com para continuar.',
}

async function onSubmit() {
  errorMessage.value = ''
  submitting.value = true
  try {
    await login(email.value, password.value)
    const redirect = typeof route.query.redirect === 'string' ? route.query.redirect : '/inicio'
    router.push(redirect)
  } catch (err) {
    errorMessage.value = ERROR_MESSAGES[err.message] || 'No se pudo iniciar sesión. Intenta de nuevo.'
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.login-view {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e6eaf3;
  padding: 1rem;
}

.login-card {
  width: 100%;
  max-width: 360px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(30, 58, 95, 0.12);
  padding: 2rem 1.75rem;
  display: flex;
  flex-direction: column;
}

.login-card__logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #1e3a5f;
  margin-bottom: 1.5rem;
}
.login-card__logo .mdi { font-size: 1.4rem; }
.login-card__logo-text {
  font-family: var(--font-display);
  font-size: 1.25rem;
}

.login-card__title {
  font-family: var(--font-display);
  font-size: 1.3rem;
  color: #1e3a5f;
  margin: 0 0 0.25rem;
}

.login-card__subtitle {
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: #64748b;
  margin: 0 0 1.5rem;
}

.login-card__label {
  font-family: var(--font-body);
  font-size: 0.8rem;
  color: #64748b;
  margin-bottom: 0.3rem;
}

.login-card__input {
  font-family: var(--font-body);
  font-size: 0.9rem;
  color: #1e3a5f;
  border: 1px solid var(--border-active);
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  margin-bottom: 1rem;
}
.login-card__input:focus {
  outline: none;
  border-color: #3b82f6;
}

.login-card__forgot {
  margin-top: 0.9rem;
  text-align: center;
  font-family: var(--font-body);
  font-size: 0.8rem;
  color: #3b82f6;
  text-decoration: none;
}
.login-card__forgot:hover { text-decoration: underline; }

.login-card__notice {
  margin: 0 0 0.75rem;
  padding: 0.5rem 0.7rem;
  border-radius: 6px;
  background: #fef3c7;
  color: #92400e;
  font-size: 12px;
}

.login-card__error {
  font-family: var(--font-body);
  font-size: 0.8rem;
  color: #e84545;
  margin: -0.5rem 0 1rem;
}

.login-card__submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: #1e3a5f;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 0.7rem;
  font-family: var(--font-body);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease;
}
.login-card__submit:hover:not(:disabled) { background: #163049; }
.login-card__submit:disabled { opacity: 0.7; cursor: default; }

.login-card__spinner {
  animation: login-spin 0.8s linear infinite;
}
@keyframes login-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>